"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _components = require("@folio/stripes/components");
var _RichSelect = _interopRequireDefault(require("../../../styles/RichSelect.css"));
var _FormattedKintMessage = _interopRequireDefault(require("../FormattedKintMessage"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const RichSelect = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let {
    ariaLabel,
    disabled = false,
    dropdownProps = {},
    id,
    input: formInput = {},
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    label,
    meta,
    onChange,
    options: userOptions = [],
    placeholder,
    renderMenu,
    renderOption,
    renderTrigger,
    required = false,
    value
  } = _ref;
  const hiddenInput = (0, _react.useRef)(null);
  const [internalValue, setInternalValue] = (0, _react.useState)(meta?.initial ?? value ?? '');
  const findSelectedOptionByValue = (0, _react.useCallback)(val => {
    return userOptions.find(opt => opt.value === val);
  }, [userOptions]);
  const [selectedOption, setSelectedOption] = (0, _react.useState)(findSelectedOptionByValue(meta?.initial ?? '') ?? {});
  (0, _react.useEffect)(() => {
    // We treat non-undefined value/formInput values as valid values, whereas undefined is only relevant when prop has not been provided
    if (value !== undefined && !(value === selectedOption?.value && value === internalValue)) {
      setSelectedOption(findSelectedOptionByValue(value));
      setInternalValue(value);
    } else if (formInput?.value !== undefined && !(value === selectedOption?.value && value === internalValue)) {
      setSelectedOption(findSelectedOptionByValue(formInput.value));
      setInternalValue(formInput.value);
    }
  }, [formInput, value, internalValue, selectedOption, findSelectedOptionByValue]);

  // Way to grab internal selectedOption state, thereby avoiding having to reparse options for it
  (0, _react.useImperativeHandle)(ref, () => ({
    selectedOption
  }));
  const defaultRenderTrigger = _ref2 => {
    let {
      onToggle,
      triggerRef,
      keyHandler,
      ariaProps,
      getTriggerProps
    } = _ref2;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.DropdownButton, {
      ref: triggerRef,
      buttonClass: _RichSelect.default.triggerButton,
      buttonStyle: "none",
      disabled: disabled,
      marginBottom0: true,
      onClick: onToggle,
      onKeyDown: keyHandler,
      paddingSide0: true,
      type: "button",
      ...getTriggerProps(),
      ...ariaProps,
      children: selectedOption?.label ?? selectedOption?.value ?? (internalValue || null) ?? placeholder ?? /*#__PURE__*/(0, _jsxRuntime.jsx)(_FormattedKintMessage.default, {
        id: "pleaseSelectOption",
        intlKey: passedIntlKey,
        intlNS: passedIntlNS
      })
    });
  };
  const handleChange = e => {
    // Actually set the value in the form (If formInput exists)
    if (formInput?.onChange) {
      formInput.onChange(e);
    }

    // If the user has set up an onChange, this will ensure that that fires
    if (onChange) {
      onChange(e, e.target.value);
    }
  };
  const changeField = val => {
    (0, _components.nativeChangeFieldValue)(hiddenInput, false, val);
  };
  const defaultRenderOption = opt => opt.label ?? opt.value;
  const defaultRenderMenu = _ref3 => {
    let {
      onToggle
    } = _ref3;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.DropdownMenu, {
      children: userOptions.map(opt => {
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
          autoFocus: selectedOption?.value === opt.value,
          buttonStyle: "dropdownItem",
          id: `${id}-${formInput.name}-option-${opt.value}`,
          marginBottom0: true,
          onClick: () => {
            // Trigger change event
            changeField(opt.value);

            // Close the menu on select
            onToggle();
          },
          children: renderOption ? renderOption(opt) : defaultRenderOption(opt)
        });
      })
    });
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [label || ariaLabel ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Label, {
      className: ariaLabel && 'sr-only',
      htmlFor: id,
      id: `${id}-label`,
      required: required,
      children: ariaLabel || label
    }) : '', /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Dropdown, {
      disabled: disabled,
      hasPadding: true,
      placement: "bottom-start",
      renderMenu: menuProps => {
        if (renderMenu) {
          return renderMenu({
            ...menuProps,
            selectedOption,
            changeField
          });
        } else {
          return defaultRenderMenu(menuProps);
        }
      },
      renderTrigger: triggerProps => {
        if (renderTrigger) {
          return renderTrigger({
            ...triggerProps,
            selectedOption
          });
        } else {
          return defaultRenderTrigger(triggerProps);
        }
      },
      ...dropdownProps
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("input", {
      ...formInput,
      ref: hiddenInput,
      hidden: true,
      onChange: handleChange,
      type: "text",
      value: internalValue
    })]
  });
});
RichSelect.propTypes = {
  ariaLabel: _propTypes.default.string,
  disabled: _propTypes.default.bool,
  dropdownProps: _propTypes.default.object,
  id: _propTypes.default.string,
  input: _propTypes.default.object,
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.func, _propTypes.default.node]),
  meta: _propTypes.default.object,
  onChange: _propTypes.default.func,
  options: _propTypes.default.arrayOf(_propTypes.default.shape({
    value: _propTypes.default.string.isRequired,
    label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node])
  })),
  placeholder: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node]),
  renderMenu: _propTypes.default.func,
  renderOption: _propTypes.default.func,
  renderTrigger: _propTypes.default.func,
  required: _propTypes.default.bool,
  value: _propTypes.default.string
};
var _default = exports.default = RichSelect;